import { DependencyList, useEffect } from 'react';

import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetOrderList } from 'src/actions';
import { GetOrderListQueryParamsDto } from 'src/dtos';
import { Order } from 'src/models';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';

interface UseOrdersReturnType {
  orderList: Order[];
  isOrderListLoading: boolean;
  hasOrderListError?: boolean;
  orderListError?: string;
  orderListTotal: number;
}

export interface UseOrdersOptions extends CustomHookBaseOptions {
  refetchOnChange?: boolean;
  queryParamsDto?: GetOrderListQueryParamsDto;
  dependency?: DependencyList;
}

const EMPTY_ORDERS: Order[] = [];

export const useOrders = (
  { skip, refetchOnChange, queryParamsDto, dependency = [] }: UseOrdersOptions = { skip: false },
): UseOrdersReturnType => {
  const dispatch = useDispatch();
  const orderList: Order[] | undefined = useSelector((state: any) => state.orders.list?.data?.data);
  const isOrderListLoading = useSelector((state: any) => Boolean(state.orders?.list?.isLoading));
  const hasOrderListFailed: boolean = useSelector((state: any) => state.orders.list?.__failed);
  const orderListError: string = useSelector((state: any) => state.orders.list?.__message);
  const payload = useSelector((state: any) => state.orders.list?.payload);
  const orderListTotal: number = useSelector((state: any) => state.orders.list?.data?.total ?? 0);

  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(doGetOrderList({ params: { accountId: account.accountId }, queryParams: queryParamsDto }));
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!orderList && !isOrderListLoading && !hasOrderListFailed) {
      fetchList();

      return;
    }

    if (
      refetchOnChange ||
      !isEqual(payload, { params: { accountId: account?.accountId }, queryParams: queryParamsDto })
    ) {
      fetchList();
    }
  }, dependency);

  return {
    orderList: Array.isArray(orderList) ? orderList : EMPTY_ORDERS,
    isOrderListLoading,
    hasOrderListError: hasOrderListFailed,
    orderListError,
    orderListTotal,
  };
};
